import { KeyRound, Lock, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { api } from '../../api';
import type { StaffListQuery, StaffMember, StaffRoleGrant, StaffStatus } from '../../api/areas/admin';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, FilterBar, StatCard, StatGrid, type Column } from '../../components/blocks';
import { EntityRef } from '../../components/EntityRef';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { cn } from '@/lib/utils';
import { formatDateTime, formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { AccessChangesPanel, NewStaffButton } from '../operator/AccessChanges';
import { FilterSelect, ListSection, RefreshButton, ResetButton, TextFilter, useAdminCrumbs, useCatalogue, useParamSetter } from './ui';

const STATUSES: StaffStatus[] = ['ACTIVE', 'SUSPENDED', 'DISABLED'];
const FILTER_KEYS = ['q', 'status', 'role', 'locked'];

export const STATUS_TONE: Record<StaffStatus, Tone> = { ACTIVE: 'ok', SUSPENDED: 'warn', DISABLED: 'neutral' };

/** The role a person holds, and what it applies to — never a scope UUID. */
export function RoleGrants({ roles, max }: { roles: StaffRoleGrant[]; max?: number }) {
  const { t } = useTranslation('admin');
  const { roleName } = useCatalogue();
  if (roles.length === 0) return <span className="text-muted-foreground">{t('staff.noRoles')}</span>;
  const shown = max ? roles.slice(0, max) : roles;
  return (
    <div className={cn('flex flex-wrap items-center gap-1', max && 'max-w-60')}>
      {shown.map((g) => (
        // The scope sits beside the badge, not inside it: a badge never wraps, and a customer
        // name is long enough to push every later column off the screen.
        <span key={`${g.role_code}/${g.scope_type}/${g.scope_id ?? ''}`} className="inline-flex max-w-full items-baseline gap-1">
          <Badge tone="info" icon={false} title={g.role_code}>
            {roleName(g.role_code, g.role_description)}
          </Badge>
          {g.scope_type === 'GLOBAL' ? null : (
            <span className="text-xs text-muted-foreground">{g.scope_name ?? t('scope.customer')}</span>
          )}
        </span>
      ))}
      {max && roles.length > max ? <span className="text-xs text-muted-foreground">{t('staff.moreRoles', { count: roles.length - max })}</span> : null}
    </div>
  );
}

/** MFA state and lockout in words, because a colour alone never says which. */
export function SignInBadges({ member }: { member: StaffMember }) {
  const { t } = useTranslation('admin');
  return (
    <div className="flex flex-wrap items-center gap-1">
      {member.mfa_enrolled ? (
        <Badge tone="ok">{t('staff.mfa.on')}</Badge>
      ) : member.mfa_required ? (
        <Badge tone="serious" title={t('staff.mfa.missingHint')}>
          {t('staff.mfa.missing')}
        </Badge>
      ) : (
        <Badge tone="warn">{t('staff.mfa.notRequired')}</Badge>
      )}
      {member.locked ? (
        <Badge tone="crit" title={member.locked_until ? t('staff.lockedUntil', { time: formatDateTime(member.locked_until) }) : undefined}>
          {t('staff.locked')}
        </Badge>
      ) : null}
    </div>
  );
}

/** `/admin/staff`: who can sign in to the console, and what they may do. */
export function StaffPage() {
  const { t } = useTranslation('admin');
  useDocumentTitle(t('staff.title'));
  const crumbs = useAdminCrumbs(t('staff.title'));
  const navigate = useNavigate();
  const [params, set, reset] = useParamSetter();
  const locked = params.get('locked');
  const q: StaffListQuery = {
    q: params.get('q') || undefined,
    status: (params.get('status') || undefined) as StaffStatus | undefined,
    role: params.get('role') || undefined,
    locked: locked === 'yes' ? true : locked === 'no' ? false : undefined,
    before: params.get('before') || undefined,
    limit: 50,
  };
  const result = useApi((signal) => api.listStaff(q, { signal }), [params.toString()]);
  const roles = useApi((signal) => api.listRoles({ signal }), []);
  const { roleName } = useCatalogue();
  const rows = result.status === 'success' ? result.data.items : [];
  const filtered = FILTER_KEYS.some((k) => params.get(k));

  const columns: Column<StaffMember>[] = [
    {
      key: 'person',
      header: t('staff.col.person'),
      cell: (r) => <EntityRef label={r.display_name} sub={r.email} to={`/admin/staff/${r.id}`} id={r.id} />,
    },
    { key: 'signIn', header: t('staff.col.signIn'), cell: (r) => <SignInBadges member={r} /> },
    // Table cells do not wrap by default; a scoped grant carries a customer name
    // long enough to push the last column off the card.
    { key: 'roles', header: t('staff.col.roles'), className: 'whitespace-normal', cell: (r) => <RoleGrants roles={r.roles} max={3} /> },
    {
      key: 'lastLogin',
      header: t('staff.col.lastLogin'),
      cell: (r) =>
        r.last_login_at ? (
          <span title={formatDateTime(r.last_login_at)}>{formatRelative(r.last_login_at)}</span>
        ) : (
          <span className="text-muted-foreground">{t('staff.neverSignedIn')}</span>
        ),
    },
    { key: 'sessions', header: t('staff.col.sessions'), numeric: true, cell: (r) => r.active_session_count },
    {
      key: 'state',
      header: t('staff.col.state'),
      cell: (r) => <Badge tone={STATUS_TONE[r.status]}>{t(`staffStatus.${r.status}`)}</Badge>,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('staff.title')}
        crumbs={crumbs}
        subtitle={t('staff.subtitle')}
        actions={
          <>
            <RefreshButton onClick={result.reload} />
            <NewStaffButton onChanged={result.reload} />
          </>
        }
      />
      <div className="space-y-4">
        <Callout tone="info" title={t('note.fourEyesTitle')}>
          {t('note.fourEyesBody')} {t('note.noSecrets')}
        </Callout>
        <AccessChangesPanel area="STAFF" reloadKey={result.status === 'success' ? result.data : undefined} onChanged={result.reload} />
        {result.status === 'success' ? (
          <StatGrid>
            <StatCard label={t('staff.stat.total')} value={rows.length} icon={Users} />
            <StatCard
              label={t('staff.stat.mfaMissing')}
              value={rows.filter((r) => !r.mfa_enrolled).length}
              tone={rows.some((r) => !r.mfa_enrolled) ? 'serious' : 'ok'}
              status={rows.some((r) => !r.mfa_enrolled) ? t('staff.mfa.missing') : t('staff.mfa.on')}
              hint={t('staff.stat.mfaMissingHint')}
              icon={KeyRound}
            />
            <StatCard
              label={t('staff.stat.locked')}
              value={rows.filter((r) => r.locked).length}
              tone={rows.some((r) => r.locked) ? 'warn' : 'ok'}
              status={rows.some((r) => r.locked) ? t('staff.locked') : undefined}
              hint={t('staff.stat.lockedHint')}
              icon={Lock}
            />
            <StatCard label={t('staff.stat.sessions')} value={rows.reduce((n, r) => n + r.active_session_count, 0)} hint={t('staff.stat.sessionsHint')} />
          </StatGrid>
        ) : null}
        <FilterBar actions={<ResetButton onClick={reset} disabled={!filtered} />}>
          <TextFilter label={t('filter.search')} value={q.q ?? ''} placeholder={t('staff.searchPlaceholder')} onApply={(v) => set('q', v)} />
          <FilterSelect
            label={t('staff.filterStatus')}
            value={q.status ?? ''}
            onChange={(v) => set('status', v)}
            options={STATUSES.map((s) => ({ value: s, label: t(`staffStatus.${s}`) }))}
          />
          <FilterSelect
            label={t('staff.filterRole')}
            value={q.role ?? ''}
            onChange={(v) => set('role', v)}
            className="min-w-44"
            options={(roles.status === 'success' ? roles.data.items : []).map((r) => ({ value: r.code, label: roleName(r.code, r.description) }))}
          />
          <FilterSelect
            label={t('staff.filterLocked')}
            value={locked ?? ''}
            onChange={(v) => set('locked', v)}
            options={[
              { value: 'yes', label: t('staff.lockedOnly') },
              { value: 'no', label: t('staff.unlockedOnly') },
            ]}
          />
        </FilterBar>
        <ListSection
          result={result}
          rows={rows}
          columns={columns}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/admin/staff/${r.id}`)}
          description={result.status === 'success' ? t('staff.count', { count: rows.length }) : undefined}
          errorTitle={t('staff.loadError')}
          empty={t('staff.empty')}
          nextBefore={result.status === 'success' ? result.data.next_before : null}
          onNext={(c) => set('before', c)}
          onFirst={() => set('before', '')}
          first={!q.before}
        />
      </div>
    </>
  );
}
